import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Lock, ExternalLink, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";

const FCNR_DATA = [
  { sno: 1,  bank: "Ujjivan SFB",        type: "Small Finance Bank",                             y1: null,    y2: null,    y3: "8.00%", y4: "8.00%", y5: "8.00%", url: "https://www.ujjivansfb.in" },
  { sno: 2,  bank: "DCB Bank",           type: "Private Sector",                                 y1: "6.90%", y2: "7.50%", y3: "7.50%", y4: "7.50%", y5: "7.00%", url: "https://www.dcbbank.com" },
  { sno: 3,  bank: "AU Small Finance",   type: "Small Finance Bank",                             y1: "5.15%", y2: "4.55%", y3: "7.40%", y4: "7.10%", y5: "7.00%", url: "https://www.aubank.in" },
  { sno: 4,  bank: "SBM Bank India",     type: "Foreign-origin (Mauritius parent)",              y1: "5.00%", y2: "4.40%", y3: "7.10%", y4: "7.10%", y5: "7.10%", url: "https://www.sbmbank.co.in" },
  { sno: 5,  bank: "Equitas Bank",       type: "Small Finance Bank",                             y1: "5.00%", y2: "5.00%", y3: "7.00%", y4: "7.00%", y5: "7.00%", url: "https://www.equitasbank.com" },
  { sno: 6,  bank: "Karur Vysya Bank",   type: "Private Sector",                                 y1: "5.00%", y2: "3.66%", y3: "7.00%", y4: "7.00%", y5: "7.00%", url: "https://www.kvb.co.in" },
  { sno: 7,  bank: "IDFC First Bank",    type: "Private Sector",                                 y1: "4.50%", y2: "4.25%", y3: "6.75%", y4: "6.75%", y5: "6.75%", url: "https://www.idfcfirstbank.com" },
  { sno: 8,  bank: "IndusInd Bank",      type: "Private Sector",                                 y1: null,    y2: null,    y3: "6.75%", y4: "6.75%", y5: "6.75%", url: "https://www.indusind.com" },
  { sno: 9,  bank: "Yes Bank",           type: "Private Sector",                                 y1: "4.50%", y2: "4.00%", y3: "6.50%", y4: "6.55%", y5: "6.60%", url: "https://www.yesbank.in" },
  { sno: 10, bank: "Punjab National Bank", type: "Public Sector",                               y1: "4.99%", y2: "4.50%", y3: "6.40%", y4: "6.45%", y5: "6.50%", url: "https://www.pnbindia.in" },
  { sno: 11, bank: "Indian Bank",        type: "Public Sector",                                  y1: null,    y2: null,    y3: "6.00%", y4: "6.25%", y5: "6.50%", url: "https://www.indianbank.in" },
  { sno: 12, bank: "Kotak Mahindra Bank", type: "Private Sector",                               y1: "4.00%", y2: "3.70%", y3: "6.25%", y4: "6.25%", y5: "6.30%", url: "https://www.kotak.com" },
  { sno: 13, bank: "ICICI Bank",         type: "Private Sector",                                 y1: "3.85%", y2: "3.85%", y3: "6.00%", y4: "6.00%", y5: "6.25%", url: "https://www.icicibank.com" },
  { sno: 14, bank: "HDFC Bank",          type: "Private Sector",                                 y1: "3.50%", y2: "3.25%", y3: "6.25%", y4: "6.00%", y5: "6.00%", url: "https://www.hdfcbank.com" },
  { sno: 15, bank: "State Bank of India", type: "Public Sector",                                y1: null,    y2: null,    y3: "6.00%", y4: "6.00%", y5: "6.00%", url: "https://sbi.co.in" },
];

const TENORS = [
  // { key: "y1", label: "1–2 Yrs" },
  // { key: "y2", label: "2–3 Yrs" },
  { key: "y3", label: "3–4 Yrs" },
  { key: "y4", label: "4–5 Yrs" },
  { key: "y5", label: "5 Yrs" },
];

function RateCell({ value, blurred }) {
  if (!value) return <span className="text-[#9aaca8]">—</span>;
  return (
    <span
      className={`font-semibold transition-all duration-500 ${
        blurred
          ? "blur-sm select-none text-[#13393E] pointer-events-none"
          : "text-[#13393E]"
      }`}
    >
      {value}
    </span>
  );
}

export default function FCNRRates() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isLoggedIn = !!user;

  const handleBankClick = (url) => {
    if (!isLoggedIn) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <section
      id="nri-fixed-deposits"
      className="py-20 md:py-32 bg-[hsl(var(--zt-beige))]"
      data-testid="fcnr-rates-section"
    >
      <div className="px-6 md:px-12 lg:px-24 max-w-screen-xl mx-auto">

        {/* Header */}
        <div className="mb-12">
          <div className="inline-flex items-center gap-2 bg-[#13393E]/8 text-[#13393E] text-xs font-medium tracking-widest uppercase px-4 py-2 rounded-full mb-4">
            <TrendingUp size={14} className="text-[#DCA520]" />
            NRI Fixed Deposits
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-light text-[#13393E] tracking-tight mb-3">
            FCNR(B) Deposit Rates
          </h2>
          <p className="text-[#4a5e5c] text-base max-w-2xl">
            Current Foreign Currency Non-Resident (Bank) deposit rates across leading Indian banks.
            Rates shown in USD equivalent tenure bands.
          </p>
        </div>

        {/* Lock banner for guests */}
        {!isLoggedIn && (
          <div className="mb-6 flex flex-col sm:flex-row items-center gap-4 bg-[#13393E] text-[hsl(var(--zt-warm-white,35,20%,96%))] rounded-xl px-6 py-4 shadow-[0_8px_30px_rgba(32,37,42,0.12)]">
            <Lock size={20} className="text-[#DCA520] shrink-0" />
            <p className="text-sm text-[#F6F3F0] flex-1">
              Register or log in to view exact rates and access bank websites directly.
            </p>
            <div className="flex gap-3 shrink-0">
              <Button
                size="sm"
                variant="outline"
                className="border-[#DCA520] text-[#DCA520] hover:bg-[#DCA520] hover:text-[#13393E] transition-all"
                onClick={() => navigate("/login")}
                data-testid="fcnr-login-btn"
              >
                Log in
              </Button>
              <Button
                size="sm"
                className="bg-[#DCA520] text-[#13393E] hover:bg-[#c4921b] transition-all"
                onClick={() => navigate("/signup")}
                data-testid="fcnr-signup-btn"
              >
                Register free
              </Button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="rounded-xl border border-[#E1D6CD] overflow-hidden shadow-[0_8px_30px_rgba(32,37,42,0.06)] bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="fcnr-table">
              <thead>
                <tr className="bg-[#13393E] text-[#F6F3F0]">
                  <th className="text-left px-4 py-4 font-medium text-xs tracking-wider uppercase w-8">#</th>
                  <th className="text-left px-4 py-4 font-medium text-xs tracking-wider uppercase">Bank</th>
                  <th className="text-left px-4 py-4 font-medium text-xs tracking-wider uppercase hidden md:table-cell">Type</th>
                  {TENORS.map((t) => (
                    <th key={t.key} className="text-center px-3 py-4 font-medium text-xs tracking-wider uppercase text-[#DCA520]">
                      {t.label}
                    </th>
                  ))}
                  <th className="text-center px-3 py-4 font-medium text-xs tracking-wider uppercase">Link</th>
                </tr>
              </thead>
              <tbody>
                {FCNR_DATA.map((row, idx) => (
                  <tr
                    key={row.sno}
                    className={`border-t border-[#E1D6CD] transition-colors duration-200 ${
                      isLoggedIn
                        ? "hover:bg-[#f7f3ee] cursor-pointer"
                        : "cursor-default"
                    } ${idx % 2 === 0 ? "bg-white" : "bg-[#faf8f5]"}`}
                    onClick={() => handleBankClick(row.url)}
                    data-testid={`fcnr-row-${row.sno}`}
                  >
                    <td className="px-4 py-3 text-[#9aaca8] text-xs">{row.sno}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`font-medium transition-all duration-500 ${
                          !isLoggedIn ? "blur-sm select-none pointer-events-none" : "text-[#13393E]"
                        }`}
                      >
                        {row.bank}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[#4a5e5c] text-xs hidden md:table-cell">
                      <span
                        className={`transition-all duration-500 ${
                          !isLoggedIn ? "blur-sm select-none pointer-events-none" : ""
                        }`}
                      >
                        {row.type}
                      </span>
                    </td>
                    {TENORS.map((t) => (
                      <td key={t.key} className="px-3 py-3 text-center">
                        <RateCell value={row[t.key]} blurred={!isLoggedIn} />
                      </td>
                    ))}
                    <td className="px-3 py-3 text-center">
                      {isLoggedIn ? (
                        <ExternalLink
                          size={15}
                          className="mx-auto text-[#DCA520] hover:text-[#13393E] transition-colors"
                          data-testid={`fcnr-link-${row.sno}`}
                        />
                      ) : (
                        <Lock size={14} className="mx-auto text-[#9aaca8]" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <p className="mt-4 text-xs text-[#9aaca8]">
          * Rates are indicative and subject to change. Verify with respective banks before investing.
          {!isLoggedIn && " Register to access bank links and full rate details."}
        </p>
      </div>
    </section>
  );
}
